import { Activity, AlertTriangle, TrendingUp } from "lucide-react";
import { useMemo } from "react";

const JournalSidebar = ({ entries }) => {
  const stats = useMemo(() => {
    if (!entries || entries.length === 0) return null;

    // Filter for TODAY's entries for "Daily Pulse"
    // For now, let's use ALL entries for aggregate stats to show more data in this demo,
    // or we could split it. Let's do "Recent Stats" (last 10 entries) or "Today".
    // Let's stick to "All Time" or "Last 30 Days" implicitly for now, or just calculate on the passed entries.
    
    // 1. Average Confidence
    const confidences = entries
      .map(e => e.confidenceLevel)
      .filter(c => c !== null && c !== undefined);
    const avgConfidence = confidences.length 
      ? (confidences.reduce((a, b) => a + b, 0) / confidences.length).toFixed(1)
      : 0;

    // 2. Mistake Counts
    const mistakeCounts = {};
    entries.forEach(e => {
      if (e.mistakesMade) {
        e.mistakesMade.split(",").forEach(m => {
          const mistake = m.trim();
          if (mistake) {
            mistakeCounts[mistake] = (mistakeCounts[mistake] || 0) + 1;
          }
        });
      }
    });

    // 3. Top Emotions
    const emotionCounts = {};
    entries.forEach(e => {
      if (e.emotionalState) {
        emotionCounts[e.emotionalState] = (emotionCounts[e.emotionalState] || 0) + 1;
      }
    });
    const topEmotions = Object.entries(emotionCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5);

    return { avgConfidence, mistakeCounts, topEmotions };
  }, [entries]);

  if (!stats) return null;

  return (
    <div className="space-y-6 animate-fade-in text-slate-200">
      
      {/* Daily Pulse Card */}
      <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
        <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Activity size={20} className="text-indigo-400" />
          Pulse
        </h2>
        
        <div className="flex items-center justify-between mb-6">
          <div className="relative h-24 w-24 flex items-center justify-center">
            {/* Simple CSS Circle for score */}
            <div className={`absolute inset-0 rounded-full border-4 border-slate-700`}></div>
            <div 
              className={`absolute inset-0 rounded-full border-4 ${stats.avgConfidence >= 7 ? 'border-emerald-500' : 'border-yellow-500'} border-t-transparent origin-center -rotate-45`}
            ></div>
            <div className="text-center">
              <span className="text-2xl font-bold text-white block">{stats.avgConfidence}</span>
              <span className="text-xs text-slate-400 uppercase">Avg Conf</span>
            </div>
          </div>
          
          <div className="flex-1 ml-6 space-y-3">
             <div className="text-sm">
                <div className="text-slate-400 mb-1">Top Mood</div>
                <div className="font-medium text-white capitalize">
                  {stats.topEmotions[0]?.[0] || "N/A"}
                </div>
             </div>
          </div>
        </div>
      </div>

      {/* Mistakes Watch Card */}
      <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
        <h2 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
          <AlertTriangle size={16} />
          Mistakes Watch
        </h2>
        
        <div className="space-y-2">
          {Object.entries(stats.mistakeCounts).length === 0 ? (
            <div className="text-xs text-slate-500 italic">No mistakes recorded yet. Good job!</div>
          ) : (
            Object.entries(stats.mistakeCounts)
              .sort(([,a], [,b]) => b - a)
              .slice(0, 5)
              .map(([mistake, count]) => (
                <div key={mistake} className="flex justify-between items-center p-2 bg-slate-900/50 rounded border border-slate-700/50">
                  <span className="text-sm text-slate-300"># {mistake}</span>
                  <span className="bg-red-500/10 text-red-400 text-xs px-2 py-0.5 rounded-full font-bold border border-red-500/20">{count}</span>
                </div>
              ))
          )}
        </div>
      </div>

       {/* Trending Tags (Emotions) */}
       <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
        <h2 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
          <TrendingUp size={16} />
          Emotional State
        </h2>
        <div className="flex flex-wrap gap-2">
          {stats.topEmotions.map(([emotion, count]) => (
             <div key={emotion} className="text-xs px-2 py-1 rounded bg-slate-700 text-slate-300 flex items-center gap-1.5">
                <span className="capitalize">{emotion}</span>
                <span className="bg-slate-600 px-1 rounded text-[10px]">{count}</span>
             </div>
          ))}
        </div>
      </div>

    </div>
  );
};

export default JournalSidebar;
